/** /subscriptions (+ /subscriptions/:id for the detail drawer)
 *
 * Ops-wide restech notification subscriptions (OCTO notifications) across
 * tenants, driven by the reusable QuerySearch query-builder widget (same
 * contract as /api-requests/, over Integration_Notification_Subscription —
 * integer row ids). The serialized query lives in `?q=`; a tenant picker
 * (`?tenant=12,845`, one or several operators) locks the query's tenant and
 * injects `tour_operator_id`. Server-side pagination + sorting; all column filtering
 * is delegated to the widget. Unlike the webhook log this is a SMALL table:
 * searches may be unbounded — no mandatory created window.
 *
 * Batch ops (manage perm): enable / disable (status toggle), set mode
 * (log_only ⇄ process), delete (remote first; failures leave rows as
 * delete_failed). "Domain sync" launches the vendor-reconcile background
 * task; "New subscription" registers a fresh callback (the row IS the opt-in).
 */
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridPaginationModel,
  type GridRowSelectionModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import * as subscriptionsApi from '@/api/notificationSubscriptions';
import type { SubscriptionListItem } from '@/api/notificationSubscriptions';
import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import type { BatchSelection } from '@/api/batch';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { type BatchActionDef, type BatchRefusals } from '@/components/DataGrid/batchTypes';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { useBatchAction } from '@/components/DataGrid/useBatchAction';
import { PageHeader } from '@/components/PageHeader';
import { PermissionGate } from '@/components/PermissionGate';
import {
  QuerySearchInput,
  equalityOperator,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { TaskDetailsDialog } from '@/features/bgtasks/TaskDetailsDialog';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { formatTenantIds } from '@/scope/tenantIds';
import { usePageTenant } from '@/scope/usePageTenant';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';

import { SetModeBatchDialog } from './SetModeBatchDialog';
import { SubscriptionCreateDialog } from './SubscriptionCreateDialog';
import { SubscriptionDetailDrawer } from './SubscriptionDetailDrawer';
import { SubscriptionSyncDialog } from './SubscriptionSyncDialog';
import { buildColumns } from './subscriptionsColumns';
import { buildSubscriptionNamespaces } from './subscriptionNamespaces';

const DEFAULT_PAGE_SIZE = 50;

const NO_TENANTS: TenantPickerValue[] = [];

const DEFAULT_HIDDEN = ['remote_id'];

/** Presets store the filter state as the raw `?q=` DSL text — it IS the
 *  canonical form (round-trips through parse/serialize), same as api-requests. */
const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q: q } : ({} as Record<string, string>);
  },
};

/** Drop every `tenant` condition from a tree (the picker owns that filter). */
function stripTenantConds(nodes: FilterNode[]): { nodes: FilterNode[]; removed: boolean } {
  let removed = false;
  const walk = (list: FilterNode[]): FilterNode[] => {
    const out: FilterNode[] = [];
    for (const n of list) {
      if (n.kind === 'cond' && n.namespace === 'tenant') {
        removed = true;
        continue;
      }
      if (n.kind === 'group') {
        const children = walk(n.children);
        if (children.length === 0) continue;
        if (children.length === 1) out.push(children[0]!);
        else out.push({ ...n, children });
        continue;
      }
      out.push(n);
    }
    return out;
  };
  return { nodes: walk(nodes), removed };
}

export function SubscriptionsPage() {
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: drawerId } = useParams<{ id?: string }>();
  const apiRef = useGridApiRef();
  const namespaces = useMemo(() => buildSubscriptionNamespaces(), []);

  // Effective tenants: URL ?tenant= > console scope (src/scope/usePageTenant.ts),
  // a list (`?tenant=12,845`) — the page takes the whole scope.
  const pageTenant = usePageTenant({ multiple: true });
  // Drives the preset defaults' tenant COLUMN only (shown unless exactly one
  // tenant is named); the filtering value is resolved below, once ?q= has been
  // parsed and scope suppression is known.
  const defaultShowTenant = pageTenant.ids.length !== 1;

  const [createOpen, setCreateOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncTaskId, setSyncTaskId] = useState<string | null>(null);

  // ⌘K "Create new subscription" quick-link (`?new`).
  useCreateIntent(useCallback(() => setCreateOpen(true), []));

  const presets = useGridPresets({
    grid: 'notification_subscriptions',
    defaultOrder: useMemo(
      () =>
        buildColumns({
          showTenant: defaultShowTenant,
          onQuickFilter: () => undefined,
          detailPath: (id) => `/subscriptions/${id}`,
        }).map((c) => c.field),
      [defaultShowTenant],
    ),
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'tenant',
    filters: PRESET_FILTERS,
  });

  const q = presets.getParam('q') ?? '';
  const ordering = presets.ordering;

  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);

  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  // A `tenant=` condition in ?q= — typed, or carried by a loaded preset —
  // already names a tenant. The ambient console scope then stands down rather
  // than AND-ing a second, conflicting tenant onto the query. An explicitly
  // PICKED tenant still wins and strips those conditions (effect below).
  const queryNamesTenant = useMemo(() => stripTenantConds(tree).removed, [tree]);
  const tenants = pageTenant.fromScope && queryNamesTenant ? NO_TENANTS : pageTenant.values;
  /** What the API's tour_operator_id param carries: `'12,845'` (D1). */
  const tenantApiValue = formatTenantIds(tenants.map((t) => t.id)) ?? undefined;
  // The create dialogs feature the page's effective tenants and pre-select the
  // one when there is exactly one (D9) — none while a `tenant=` condition in ?q=
  // has the scope stand down.
  const pageTenants = pageTenantsFrom(tenantApiValue, tenants);

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [q, pageTenant.raw]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  // When a tenant is picked and the tree still carries tenant conditions,
  // strip them and rewrite ?q= (the picker is authoritative for tenant).
  useEffect(() => {
    // Never for a scope-derived tenant: the console scope must not rewrite the
    // user's (or a preset's) query — that would also diverge a loaded preset.
    if (!tenantApiValue || pageTenant.fromScope) return;
    const { nodes, removed } = stripTenantConds(tree);
    if (removed) {
      const next = new URLSearchParams(sp);
      presets.beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
      snackbar.show('Tenant filter replaced by the picker above.', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantApiValue, tree]);

  const onTenantChange = pageTenant.setTenants;

  // Quick filters from cell popovers: append an equality chip for the
  // namespace (tenant goes through the picker instead — its chip would be
  // stripped right back out).
  const onQuickFilter = useCallback(
    (namespace: string, value: string) => {
      if (namespace === 'tenant') {
        pageTenant.setRaw(value);
        return;
      }
      const cfg = namespaces[namespace];
      const operator = cfg ? equalityOperator(cfg.operators) : null;
      if (!operator) return;
      setQuery([...tree, { kind: 'cond', namespace, operator, value }]);
    },
    [namespaces, tree, setQuery, pageTenant],
  );

  // Drawer routes keep the grid's query string so closing the drawer restores
  // the exact search.
  const detailPath = useCallback(
    (id: number) => `/subscriptions/${id}${location.search}`,
    [location.search],
  );

  const lockedFilters = tenantApiValue
    ? { tenant: { operator: 'iexact', value: tenantApiValue } }
    : undefined;
  // The suggestion endpoints here are the bookings autocompletes, which take the list.
  const extraFilterValues = tenantApiValue ? { tour_operator_id: tenantApiValue } : undefined;

  // Toolbar Refresh + auto-refresh. Polling pauses on the extended-timeout
  // tier (each such request is audited at the maximum statement budget).
  const autoRefresh = useGridAutoRefresh({ grid: 'notification_subscriptions' });
  const query = useQuery({
    queryKey: [
      'subscriptions',
      pagination.page,
      pagination.pageSize,
      ordering,
      tenantApiValue ?? null,
      q,
      extendedTimeout,
    ],
    queryFn: () =>
      subscriptionsApi.list({
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        ordering,
        ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
        filters: toWireAst(tree),
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    enabled: !presets.blocking,
  });

  // -------------------------------------------------------------------------
  // Batch: enable / disable / set mode / delete — raw DataGridPro, so the
  // batch layer is wired by hand (the api-requests convention), integer ids.
  // -------------------------------------------------------------------------
  const rows = useMemo(() => query.data?.results ?? [], [query.data?.results]);

  // Small table → the backend runs a plain sync count; no deferred-count mode.
  const total = query.data && query.data.count >= 0 ? query.data.count : rows.length;

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);

  const queryIdentity = useMemo(
    () => JSON.stringify({ q, tenant: tenantApiValue, ordering, pageSize: pagination.pageSize }),
    [q, tenantApiValue, ordering, pagination.pageSize],
  );

  const lastIdentityRef = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentityRef.current !== queryIdentity) {
      lastIdentityRef.current = queryIdentity;
      if (allMatching) setAllMatching(false);
      setExtendedTimeout(false);
    }
  }, [queryIdentity, allMatching]);

  const selectedIds = useMemo(() => selectionModel.map((i) => Number(i)), [selectionModel]);
  const selectedCount = allMatching ? total : selectedIds.length;

  const clearSelection = useCallback(() => {
    setSelectionModel([]);
    setAllMatching(false);
  }, []);

  const handleSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setSelectionModel(model);
    setAllMatching((prev) => (prev ? false : prev));
  }, []);

  const filterQuery = useMemo(() => {
    const usp = new URLSearchParams();
    if (ordering) usp.set('ordering', ordering);
    if (tenantApiValue) usp.set('tour_operator_id', tenantApiValue);
    const wire = toWireAst(tree);
    if (wire.length > 0) usp.set('filters', JSON.stringify(wire));
    return usp.toString();
  }, [ordering, tenantApiValue, tree]);

  const batchAction = useBatchAction({
    resource: 'notification_subscriptions',
    queryKey: ['subscriptions'],
    onCleared: clearSelection,
  });

  const runStandard = useCallback(
    (
      action: string,
      label: string,
      selection: BatchSelection,
      params?: Record<string, unknown>,
      refusals?: BatchRefusals,
      pinnedQuery?: string,
    ) => batchAction.run({ action, label, selection, params, refusals, filterQuery: pinnedQuery ?? filterQuery }),
    [batchAction, filterQuery],
  );

  const batchActions = useMemo<BatchActionDef[]>(
    () => [
      {
        key: 'enable',
        label: 'Enable',
        action: 'enable',
        permission: 'console_restech_manage',
        confirmText: (n) =>
          `Enable ${n.toLocaleString()} subscription(s)? Disabled rows go back to active; deliveries resume.`,
      },
      {
        key: 'disable',
        label: 'Disable',
        action: 'disable',
        permission: 'console_restech_manage',
        confirmText: (n) =>
          `Disable ${n.toLocaleString()} subscription(s)? Deliveries are dropped until re-enabled (the vendor registration stays).`,
      },
      {
        key: 'set_mode',
        label: 'Set mode…',
        action: 'set_mode',
        permission: 'console_restech_manage',
        dialog: (ctx) => <SetModeBatchDialog ctx={ctx} />,
      },
      {
        key: 'delete',
        label: 'Delete',
        action: 'delete',
        destructive: true,
        permission: 'console_restech_manage',
        confirmText: (n) =>
          `Delete ${n.toLocaleString()} subscription(s)? The vendor-side subscription is deleted first; failures keep the row as delete_failed. Runs in the background and can't be undone.`,
      },
    ],
    [],
  );

  const batchEnabled = batchActions.length > 0;

  const columns = useMemo(
    () =>
      orderColumns(
        buildColumns({ showTenant: tenants.length !== 1, onQuickFilter, detailPath }),
        presets.orderedFields,
      ),
    [tenants.length, onQuickFilter, detailPath, presets.orderedFields],
  );

  const apiError =
    query.error instanceof ConsoleApiError &&
    query.error.status === 400 &&
    !isStatementTimeout(query.error)
      ? query.error
      : null;
  const backendMessage =
    (apiError?.body as { filters?: string } | undefined)?.filters ?? apiError?.message ?? null;

  const shownOnce = useRef(false);
  useEffect(() => {
    if (backendMessage && !shownOnce.current) {
      shownOnce.current = true;
      snackbar.show(`Search rejected: ${backendMessage}`, 'error');
    }
    if (!backendMessage) shownOnce.current = false;
  }, [backendMessage, snackbar]);

  const hasSearch = q.trim().length > 0 || tenants.length > 0;

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="subscriptions"
        title="Webhook subscriptions"
        subtitle="Restech notification subscriptions (OCTO) across every tenant — a row is an opt-in: it binds a callback, a credential and a vendor registration."
        actions={
          <PermissionGate perm="console_restech_manage">
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={() => setSyncOpen(true)}
              >
                Domain sync
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                New subscription
              </Button>
            </Stack>
          </PermissionGate>
        }
      />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'flex-start' }}>
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <TenantPicker multiple value={tenants} onChange={onTenantChange} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <QuerySearchInput
            namespaces={namespaces}
            value={tree}
            onChange={setQuery}
            {...(lockedFilters ? { lockedFilters } : {})}
            {...(extraFilterValues ? { extraFilterValues } : {})}
            placeholder="Search subscriptions…  e.g. restech=ventrata & status=create_failed"
          />
        </Box>
      </Stack>

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}

      {backendMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {backendMessage}
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<SubscriptionListItem>
          apiRef={apiRef}
          rows={rows}
          rowCount={total}
          getRowId={(r) => r.id}
          columns={columns}
          loading={query.isLoading || query.isFetching}
          sx={{ flex: 1, minHeight: 360 }}
          onRowClick={(p) => navigate(detailPath(p.row.id))}
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          pageSizeOptions={[25, 50, 100, 200]}
          checkboxSelection={batchEnabled}
          disableRowSelectionOnClick
          disableColumnFilter
          {...(batchEnabled
            ? {
                rowSelectionModel: selectionModel,
                onRowSelectionModelChange: handleSelectionChange,
                keepNonExistentRowsSelected: true,
              }
            : {})}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {hasSearch
                  ? 'No subscriptions match this search.'
                  : 'No notification subscriptions yet — create one to opt a tenant in.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            toolbar: {
              refresh: gridRefreshToolbar(query, autoRefresh, { paused: extendedTimeout }),
              hideFilterButton: true,
              exportFileName: 'subscriptions',
              presets: presets.toolbar,
              ...(batchEnabled
                ? {
                    batch: {
                      actions: batchActions,
                      ids: selectedIds,
                      allMatching,
                      count: selectedCount,
                      matched: total,
                      filterQuery,
                      runStandard,
                      clearSelection,
                      onSelectAllMatching: () => setAllMatching(true),
                      isPending: batchAction.isPending,
                    },
                  }
                : {}),
            } as GridToolbarProps,
          }}
        />
      )}

      {drawerId && (
        <SubscriptionDetailDrawer
          subscriptionId={Number(drawerId)}
          open
          onClose={() => navigate(`/subscriptions${location.search}`)}
        />
      )}

      <SubscriptionCreateDialog
        open={createOpen}
        pageTenants={pageTenants}
        onClose={() => setCreateOpen(false)}
        onCreated={(row) => navigate(`/subscriptions/${row.id}${location.search}`)}
      />

      <SubscriptionSyncDialog
        open={syncOpen}
        pageTenants={pageTenants}
        onClose={() => setSyncOpen(false)}
        onLaunched={setSyncTaskId}
      />

      {syncTaskId && (
        <TaskDetailsDialog taskId={syncTaskId} onClose={() => setSyncTaskId(null)} />
      )}
    </Box>
  );
}
